import React, { useCallback, useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Card,
  CardMedia,
  CardActions,
  Chip,
  Alert,
  Modal,
  Backdrop,
  Fade,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
} from '@mui/icons-material';
import { ImagenProducto } from '../../../types/producto.types';
import { formatearTamaño } from '../../../utils';

interface ImagenesProductoProps {
  imagenes: ImagenProducto[];
  onImagenesChange: (imagenes: ImagenProducto[]) => void;
  error?: string;
  maxImagenes?: number;
  maxTamañoMB?: number;
  transitionEffect?: 'cube' | 'fade' | 'slide';
  transitionDuration?: number;
}

export const ImagenesProducto: React.FC<ImagenesProductoProps> = ({
  imagenes,
  onImagenesChange,
  error,
  maxImagenes = 7,
  maxTamañoMB = 5,
  transitionEffect = 'cube',
  transitionDuration = 400,
}) => {
  const carruselRef = useRef<HTMLDivElement>(null);
  const cubeRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);

  const updateScrollButtons = useCallback(() => {
    if (!carruselRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = carruselRef.current;
    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
    
    // Calcular índice actual aproximado
    const itemWidth = 216; // 200px + 16px gap
    const newIndex = Math.round(scrollLeft / itemWidth);
    setCurrentIndex(Math.min(newIndex, imagenes.length - 1));
  }, [imagenes.length]);

  const scrollToLeft = () => {
    if (transitionEffect === 'cube' && cubeRef.current) {
      setCurrentIndex(prev => Math.max(0, prev - 1));
    } else if (carruselRef.current) {
      carruselRef.current.scrollBy({
        left: -432,
        behavior: 'smooth'
      });
    }
  };

  const scrollToRight = () => {
    if (transitionEffect === 'cube' && cubeRef.current) {
      setCurrentIndex(prev => Math.min(imagenes.length - 1, prev + 1));
    } else if (carruselRef.current) {
      carruselRef.current.scrollBy({
        left: 432,
        behavior: 'smooth'
      });
    }
  };

  // Manejo de eventos táctiles para swipe
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (carruselRef.current?.offsetLeft || 0));
    setScrollLeft(carruselRef.current?.scrollLeft || 0);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !carruselRef.current) return;
    e.preventDefault();
    const x = e.pageX - (carruselRef.current.offsetLeft || 0);
    const walk = (x - startX) * 2;
    carruselRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - (carruselRef.current?.offsetLeft || 0));
    setScrollLeft(carruselRef.current?.scrollLeft || 0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !carruselRef.current) return;
    const x = e.touches[0].pageX - (carruselRef.current.offsetLeft || 0);
    const walk = (x - startX) * 2;
    carruselRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Funciones para el modal de ampliación
  const openModal = (index: number) => {
    setSelectedImageIndex(index);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const nextImageInModal = useCallback(() => {
    setSelectedImageIndex((prev) => (prev + 1) % imagenes.length);
  }, [imagenes.length]);

  const prevImageInModal = useCallback(() => {
    setSelectedImageIndex((prev) => (prev - 1 + imagenes.length) % imagenes.length);
  }, [imagenes.length]);

  // Navegación con teclado en el modal
  const handleModalKeyDown = useCallback((e: KeyboardEvent) => {
    if (!modalOpen) return;
    
    if (e.key === 'ArrowLeft') {
      prevImageInModal();
    } else if (e.key === 'ArrowRight') {
      nextImageInModal();
    } else if (e.key === 'Escape') {
      closeModal();
    }
  }, [modalOpen, prevImageInModal, nextImageInModal]);

  // Navegación con teclado
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      scrollToLeft();
    } else if (e.key === 'ArrowRight') {
      scrollToRight();
    }
  }, []);

  // Agregar/remover event listeners para teclado
  React.useEffect(() => {
    if (imagenes.length > 0) {
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keydown', handleModalKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keydown', handleModalKeyDown);
      };
    }
  }, [imagenes.length, handleKeyDown, handleModalKeyDown]);

  // Funciones para drag and drop
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Solo quitar el estado si realmente salimos del área
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Asegurar que el estado drag over esté activo
    if (!isDragOver) {
      setIsDragOver(true);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Verificar límite de imágenes
    if (imagenes.length + files.length > maxImagenes) {
      alert(`Solo puedes subir máximo ${maxImagenes} imágenes`);
      return;
    }

    procesarArchivos(files);
  };

  // Función centralizada para procesar archivos (tanto desde input como desde drag&drop)
  const procesarArchivos = (files: File[]) => {
    const nuevasImagenes: ImagenProducto[] = [];
    let archivosProcessed = 0;

    files.forEach((file) => {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        alert(`Error en ${file.name}: El archivo debe ser una imagen`);
        archivosProcessed++;
        return;
      }

      // Validar tamaño
      const tamañoMB = file.size / (1024 * 1024);
      if (tamañoMB > maxTamañoMB) {
        alert(`Error en ${file.name}: La imagen debe ser menor a ${maxTamañoMB}MB`);
        archivosProcessed++;
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const nuevaImagen: ImagenProducto = {
          id: `${Date.now()}-${Math.random()}`,
          file,
          preview: e.target?.result as string,
          nombre: file.name,
          tamaño: file.size,
          tipo: file.type,
        };

        nuevasImagenes.push(nuevaImagen);
        archivosProcessed++;

        // Cuando todos los archivos estén procesados
        if (archivosProcessed === files.length) {
          onImagenesChange([...imagenes, ...nuevasImagenes]);
          setTimeout(updateScrollButtons, 100);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // Función para manejar cambios en el input file
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Verificar límite de imágenes
    if (imagenes.length + files.length > maxImagenes) {
      alert(`Solo puedes subir máximo ${maxImagenes} imágenes`);
      return;
    }

    procesarArchivos(files);
    
    // Limpiar el input
    event.target.value = '';
  };

  const eliminarImagen = (id: string) => {
    const imagenesActualizadas = imagenes.filter((img) => img.id !== id);
    onImagenesChange(imagenesActualizadas);
    // Actualizar botones de scroll después de eliminar
    setTimeout(updateScrollButtons, 100);
  };

  // Actualizar botones cuando las imágenes cambien
  React.useEffect(() => {
    updateScrollButtons();
  }, [imagenes, updateScrollButtons]);

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 2,
          fontFamily: 'Poppins, sans-serif',
          color: '#666666',
          fontWeight: 600,
        }}
      >
        Imágenes del Producto ({imagenes.length}/{maxImagenes})
      </Typography>

      {/* Botón de subida */}
      <Button
        variant="outlined"
        component="label"
        startIcon={<UploadIcon />}
        disabled={imagenes.length >= maxImagenes}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          width: '100%',
          height: 120,
          borderStyle: 'dashed',
          borderWidth: isDragOver ? 3 : 2,
          borderColor: isDragOver 
            ? '#a0588e' 
            : error 
              ? '#d32f2f' 
              : '#a0588e',
          color: isDragOver 
            ? '#a0588e' 
            : error 
              ? '#d32f2f' 
              : '#a0588e',
          backgroundColor: isDragOver
            ? 'rgba(160, 88, 142, 0.08)'
            : imagenes.length >= maxImagenes 
              ? '#f5f5f5' 
              : 'transparent',
          transform: isDragOver ? 'scale(1.02)' : 'scale(1)',
          '&:hover': {
            backgroundColor: imagenes.length >= maxImagenes 
              ? '#f5f5f5' 
              : isDragOver 
                ? 'rgba(160, 88, 142, 0.12)' 
                : 'rgba(160, 88, 142, 0.04)',
            transform: isDragOver ? 'scale(1.02)' : 'scale(1.01)',
          },
          transition: 'all 0.2s ease',
          mb: 2,
          position: 'relative',
          '&::after': isDragOver ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle, rgba(160, 88, 142, 0.1) 0%, transparent 70%)',
            borderRadius: 'inherit',
            pointerEvents: 'none',
          } : {},
        }}
      >
        {isDragOver 
          ? '📥 Suelta las imágenes aquí'
          : imagenes.length >= maxImagenes
            ? `Máximo ${maxImagenes} imágenes alcanzado`
            : '📤 Seleccionar Imágenes o arrastra aquí'}
        <input
          type="file"
          hidden
          accept="image/*"
          multiple
          onChange={handleFileChange}
          disabled={imagenes.length >= maxImagenes}
        />
      </Button>

      {/* Mensaje de error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Información sobre límites */}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 2,
          color: '#666666',
          textAlign: 'center',
        }}
      >
        Máximo {maxImagenes} imágenes • Tamaño máximo {maxTamañoMB}MB por imagen
        <br />
        <Box component="span" sx={{ color: '#a0588e', fontWeight: 500 }}>
          💡 Tip: También puedes arrastrar las imágenes directamente sobre el área de subida
        </Box>
      </Typography>

      {/* Carrusel de imágenes */}
      {imagenes.length > 0 && (
        <Box sx={{ position: 'relative' }}>
          {/* Botón anterior */}
          {canScrollLeft && (
            <IconButton
              onClick={scrollToLeft}
              sx={{
                position: 'absolute',
                left: -20,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                color: '#a0588e',
                border: '2px solid #a0588e',
                width: 40,
                height: 40,
                '&:hover': {
                  backgroundColor: '#a0588e',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(160, 88, 142, 0.3)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ChevronLeftIcon fontSize="medium" />
            </IconButton>
          )}

          {/* Botón siguiente */}
          {canScrollRight && (
            <IconButton
              onClick={scrollToRight}
              sx={{
                position: 'absolute',
                right: -20,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                color: '#a0588e',
                border: '2px solid #a0588e',
                width: 40,
                height: 40,
                '&:hover': {
                  backgroundColor: '#a0588e',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(160, 88, 142, 0.3)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ChevronRightIcon fontSize="medium" />
            </IconButton>
          )}

          {/* Contenedor del carrusel - Cube 3D o Scroll */}
          {transitionEffect === 'cube' ? (
            // Efecto Cube 3D
            <Box
              sx={{
                perspective: '1000px',
                width: '100%',
                height: 250,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                ref={cubeRef}
                sx={{
                  position: 'relative',
                  width: 200,
                  height: 200,
                  transformStyle: 'preserve-3d',
                  transform: 'rotateX(0deg) rotateY(0deg)',
                  transition: `transform ${transitionDuration}ms cubic-bezier(0.4, 0.0, 0.2, 1)`,
                }}
              >
                {imagenes.map((imagen, index) => {
                  const anglePerImage = 360 / Math.max(imagenes.length, 1);
                  const angle = anglePerImage * index;
                  const radius = 150;
                  const yRotation = -currentIndex * anglePerImage;

                  return (
                    <Box
                      key={imagen.id}
                      sx={{
                        position: 'absolute',
                        width: 200,
                        height: 200,
                        left: 0,
                        top: 0,
                        backfaceVisibility: 'hidden',
                        transformStyle: 'preserve-3d',
                        transform: `rotateY(${angle}deg) translateZ(${radius}px)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Card
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: '100%',
                          '&:hover .delete-button': {
                            opacity: 1,
                          },
                          '&:hover .zoom-button': {
                            opacity: 1,
                          },
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="200"
                          image={imagen.preview}
                          alt={imagen.nombre}
                          onClick={() => openModal(index)}
                          sx={{
                            objectFit: 'cover',
                            cursor: 'pointer',
                            width: '100%',
                            height: '100%',
                          }}
                        />

                        <IconButton
                          className="zoom-button"
                          onClick={() => openModal(index)}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            '&:hover': {
                              backgroundColor: 'rgba(160, 88, 142, 0.8)',
                            },
                          }}
                          size="small"
                        >
                          <ZoomInIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          className="delete-button"
                          onClick={() => eliminarImagen(imagen.id)}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            '&:hover': {
                              backgroundColor: 'rgba(211, 47, 47, 0.8)',
                            },
                          }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Card>
                    </Box>
                  );
                })}
              </Box>
              <style>{`
                @keyframes cubeSpin {
                  from { transform: rotateY(0deg); }
                  to { transform: rotateY(360deg); }
                }
              `}</style>
            </Box>
          ) : (
            // Scroll normal
            <Box
              ref={carruselRef}
              onScroll={updateScrollButtons}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                scrollBehavior: isDragging ? 'auto' : 'smooth',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                pb: 1,
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none',
              }}
            >
              {imagenes.map((imagen, index) => (
                <Box
                  key={imagen.id}
                  sx={{
                    minWidth: 200,
                    maxWidth: 200,
                    flexShrink: 0,
                  }}
                >
                  <Card
                    sx={{
                      position: 'relative',
                      height: '100%',
                      '&:hover .delete-button': {
                        opacity: 1,
                      },
                      '&:hover .zoom-button': {
                        opacity: 1,
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="150"
                      image={imagen.preview}
                      alt={imagen.nombre}
                      onClick={() => openModal(index)}
                      sx={{
                        objectFit: 'cover',
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                      }}
                    />

                    <IconButton
                      className="zoom-button"
                      onClick={() => openModal(index)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgba(160, 88, 142, 0.8)',
                        },
                      }}
                      size="small"
                    >
                      <ZoomInIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      className="delete-button"
                      onClick={() => eliminarImagen(imagen.id)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgba(211, 47, 47, 0.8)',
                        },
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>

                    <CardActions sx={{ padding: 1, flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: '#333',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%',
                        }}
                      >
                        {imagen.nombre}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        <Chip
                          icon={<ImageIcon />}
                          label={formatearTamaño(imagen.tamaño)}
                          size="small"
                          sx={{
                            fontSize: '0.75rem',
                            height: 24,
                            '& .MuiChip-icon': {
                              fontSize: '0.875rem',
                            },
                          }}
                        />
                      </Box>
                    </CardActions>
                  </Card>
                </Box>
              ))}
            </Box>
          )}

          {/* Indicador de posición */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 2,
              gap: 0.5,
            }}
          >
            {imagenes.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: index === currentIndex ? '#a0588e' : '#e0e0e0',
                  transition: 'background-color 0.2s',
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Estado vacío */}
      {imagenes.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            border: '2px dashed #e0e0e0',
            borderRadius: 2,
            backgroundColor: '#fafafa',
          }}
        >
          <ImageIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
          <Typography variant="body2" color="textSecondary">
            No hay imágenes seleccionadas
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Haz clic en "Seleccionar Imágenes" para agregar fotos del producto
          </Typography>
        </Box>
      )}

      {/* Modal de ampliación de imagen */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
          },
        }}
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '90vw',
              maxHeight: '90vh',
              outline: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Botón cerrar */}
            <IconButton
              onClick={closeModal}
              sx={{
                position: 'absolute',
                top: -60,
                right: 0,
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(160, 88, 142, 0.8)',
                },
                zIndex: 1,
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Controles de navegación */}
            {imagenes.length > 1 && (
              <>
                <IconButton
                  onClick={prevImageInModal}
                  sx={{
                    position: 'absolute',
                    left: -60,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(160, 88, 142, 0.8)',
                    },
                    zIndex: 1,
                  }}
                >
                  <ChevronLeftIcon fontSize="large" />
                </IconButton>

                <IconButton
                  onClick={nextImageInModal}
                  sx={{
                    position: 'absolute',
                    right: -60,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(160, 88, 142, 0.8)',
                    },
                    zIndex: 1,
                  }}
                >
                  <ChevronRightIcon fontSize="large" />
                </IconButton>
              </>
            )}

            {/* Imagen ampliada */}
            <Box
              component="img"
              src={imagenes[selectedImageIndex]?.preview}
              alt={imagenes[selectedImageIndex]?.nombre}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              }}
            />

            {/* Información de la imagen */}
            <Box
              sx={{
                position: 'absolute',
                bottom: -80,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                textAlign: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                padding: 2,
                borderRadius: 1,
                minWidth: 200,
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                {imagenes[selectedImageIndex]?.nombre}
              </Typography>
              <Typography variant="caption">
                {formatearTamaño(imagenes[selectedImageIndex]?.tamaño || 0)} • 
                Imagen {selectedImageIndex + 1} de {imagenes.length}
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}; 